import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Tooltip from 'components/v2/Tooltip';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
const Adjustments = ({ items, onSelect, selectedItems, isItemsUnmapped }) => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const sum = items.reduce((s, item) => {
        return s + item.value || 0;
    }, 0);
    const totalShipperCost = isItemsUnmapped
        ? items.reduce((total, item) => {
            if (!item.shipper_cost)
                return total;
            return total + item.shipper_cost;
        }, 0)
        : 'N/A';
    const totalCarrierCost = isItemsUnmapped
        ? items.reduce((total, item) => {
            if (!item.carrier_cost)
                return total;
            return total + item.carrier_cost;
        }, 0)
        : 'N/A';
    return (React.createElement("div", null,
        React.createElement("table", { className: "default-table" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, t('DESCRIPTION', { count: 1 })),
                    isItemsUnmapped && React.createElement("th", null, t('SHIPPER_COST')),
                    isItemsUnmapped && React.createElement("th", null, t('CARRIER_COST')),
                    !isItemsUnmapped && React.createElement("th", null, t('COST')),
                    !isItemsUnmapped && React.createElement("th", null, titleize(t('DATE', { count: 1 }))),
                    React.createElement("th", null, t('SELECT')))),
            React.createElement("tbody", null,
                items.map((item) => {
                    return (React.createElement("tr", { key: item._id },
                        React.createElement("td", { className: "" }, t(sluggify(item.message))),
                        !isItemsUnmapped && (React.createElement("td", { className: "" }, formatMoney(item.value, item === null || item === void 0 ? void 0 : item.currency))),
                        !isItemsUnmapped && (React.createElement("td", { className: "" }, moment(item.timestamp).format('L LT'))),
                        isItemsUnmapped && (React.createElement("td", { className: "" }, formatMoney(item.shipper_cost, (item === null || item === void 0 ? void 0 : item.currency) || DEFAULT_CURRENCY))),
                        isItemsUnmapped && (React.createElement("td", { className: "" }, formatMoney(item.carrier_cost, (item === null || item === void 0 ? void 0 : item.currency) || DEFAULT_CURRENCY))),
                        React.createElement("td", { className: "" },
                            React.createElement("input", { type: "checkbox", checked: selectedItems.includes(item._id), onClick: (e) => onSelect(item, e.target.checked) }))));
                }),
                React.createElement("tr", null,
                    React.createElement("td", null, t('TOTAL')),
                    !isItemsUnmapped && (React.createElement("td", null, formatMoney(sum, ((_a = items[0]) === null || _a === void 0 ? void 0 : _a.currency) || DEFAULT_CURRENCY))),
                    isItemsUnmapped && (React.createElement("td", null, formatMoney(totalShipperCost, ((_b = items[0]) === null || _b === void 0 ? void 0 : _b.currency) || DEFAULT_CURRENCY))),
                    isItemsUnmapped && (React.createElement("td", null, formatMoney(totalCarrierCost, ((_c = items[0]) === null || _c === void 0 ? void 0 : _c.currency) || DEFAULT_CURRENCY))),
                    !isItemsUnmapped && React.createElement("td", null, "\u00A0"),
                    React.createElement("td", null, "\u00A0"))))));
};
export default function OrphanAdjustments({ receivables, payables, unmappedAccessorialRequests, onSelect, selectedReceivables, selectedPayables, selectedUnmappedAccessorialRequests, }) {
    const { t } = useTranslation();
    const receivablesCount = receivables.length;
    const payablesCount = payables.length;
    const unmappedAccessorialRequestsCount = unmappedAccessorialRequests.length;
    if (receivablesCount === 0 &&
        payablesCount === 0 &&
        unmappedAccessorialRequestsCount === 0) {
        return null;
    }
    return (React.createElement("div", null,
        React.createElement("div", { className: "flex items-center space-x-2" },
            React.createElement("h4", { className: "my-0" }, t('UNAPPLIED_ADJUSTMENTS')),
            React.createElement(Tooltip, { id: "unapplied-adjustments-note" }, t('UNAPPLIED_ADJUSTMENTS_TOOLTIP'))),
        receivablesCount > 0 && (React.createElement("div", { className: "mt-4" },
            React.createElement("div", { className: "border-solid border-b border-grey-light" },
                React.createElement("h4", { className: "my-0 mb-2" }, t('SHIPPER_CHARGES'))),
            React.createElement(Adjustments, { onSelect: (item, checked) => onSelect('receivables', item, checked), items: receivables, selectedItems: selectedReceivables, isItemsUnmapped: false }))),
        payablesCount > 0 && (React.createElement("div", { className: "mt-4" },
            React.createElement("div", { className: "border-solid border-b border-grey-light" },
                React.createElement("h4", { className: "my-0 mb-2" }, t('CARRIER_CHARGES'))),
            React.createElement(Adjustments, { onSelect: (item, checked) => onSelect('payables', item, checked), items: payables, selectedItems: selectedPayables, isItemsUnmapped: false }))),
        unmappedAccessorialRequestsCount > 0 && (React.createElement("div", { className: "mt-4" },
            React.createElement("div", { className: "mb-4 border-solid border-b border-grey-light flex items-center space-x-2" },
                React.createElement("h4", { className: "my-0" }, t('UNMAPPED_ACCESSORIALS')),
                React.createElement(Tooltip, { id: "unapplied-adjustments-note" }, t('UNMAPPED_ACCESSORIALS_TOOLTIP'))),
            React.createElement(Adjustments, { onSelect: (item, checked) => onSelect('accessorials', item, checked), items: unmappedAccessorialRequests, selectedItems: selectedUnmappedAccessorialRequests, isItemsUnmapped: true })))));
}
OrphanAdjustments.propTypes = {
    receivables: PropTypes.array,
    payables: PropTypes.array,
    unmappedAccessorialRequests: PropTypes.array,
    selectedPayables: PropTypes.array,
    selectedReceivables: PropTypes.array,
    selectedUnmappedAccessorialRequests: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
};
OrphanAdjustments.defaultProps = {
    receivables: [],
    payables: [],
    unmappedAccessorialRequests: [],
    selectedPayables: [],
    selectedReceivables: [],
    selectedUnmappedAccessorialRequests: [],
};
